/**
 * Centralized logging utility
 * Provides environment-aware logging with different levels
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

class Logger {
  private static instance: Logger;
  private readonly logLevel: LogLevel;

  private constructor() {
    // Set log level based on environment
    const isDevelopment = process.env.NODE_ENV === 'development';
    const debugMode = process.env.NEXT_PUBLIC_DEBUG === 'true';
    
    if (debugMode) {
      this.logLevel = LogLevel.DEBUG;
    } else if (isDevelopment) {
      this.logLevel = LogLevel.INFO;
    } else {
      this.logLevel = LogLevel.ERROR; // Production: only errors
    }
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private shouldLog(level: LogLevel): boolean {
    return level <= this.logLevel;
  }

  private formatMessage(level: string, message: string, context?: string): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? `[${context}]` : '';
    return `[${timestamp}] ${level} ${contextStr} ${message}`;
  }

  public error(message: string, error?: Error | string | unknown, context?: string): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const formattedMessage = this.formatMessage('ERROR', message, context);
      if (error) {
        console.error(formattedMessage, error);
      } else {
        console.error(formattedMessage);
      }
    }
  }

  public warn(message: string, context?: string): void {
    if (this.shouldLog(LogLevel.WARN)) {
      const formattedMessage = this.formatMessage('WARN', message, context);
      console.warn(formattedMessage);
    }
  }

  public info(message: string, context?: string): void {
    if (this.shouldLog(LogLevel.INFO)) {
      const formattedMessage = this.formatMessage('INFO', message, context);
      console.info(formattedMessage);
    }
  }

  public debug(message: string, data?: unknown, context?: string): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      const formattedMessage = this.formatMessage('DEBUG', message, context);
      if (data !== undefined) {
        console.log(formattedMessage, data);
      } else {
        console.log(formattedMessage);
      }
    }
  }

  // Performance timing utilities
  public time(label: string): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.time(`[PERF] ${label}`);
    }
  }

  public timeEnd(label: string): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.timeEnd(`[PERF] ${label}`);
    }
  }
}

// Create singleton instance and export convenience methods
const logger = Logger.getInstance();

export const log = {
  error: (message: string, error?: Error | string | unknown, context?: string) => logger.error(message, error, context),
  warn: (message: string, context?: string) => logger.warn(message, context),
  info: (message: string, context?: string) => logger.info(message, context),
  debug: (message: string, data?: unknown, context?: string) => logger.debug(message, data, context),
  time: (label: string) => logger.time(label),
  timeEnd: (label: string) => logger.timeEnd(label),
};

export default logger;
